import {forEach, identity, omit} from 'lodash';

import {isShallowEqual} from '../../helpers/reselectHelpers';
import {AppAction, AppActionsEnum} from '../appActions';
import {DataCascade} from '../data/dataCascade';
import {accountDetailsPanelReducer} from './accounts/accountDetailsPanelReducer';
import {AccountDetailsPanelState} from './accounts/accountDetailsPanelState';
import {analyticsPanelReducer} from './analytics/analyticsPanelReducer';
import {AnalyticsPanelState} from './analytics/analyticsPanelState';
import {appPanelReducer} from './app/appPanelReducer';
import {AppPanelState} from './app/appPanelState';
import {calendarsPanelReducer} from './calendars/calendarsPanelReducer';
import {CalendarsPanelState} from './calendars/calendarsPanelState';
import {cellsPanelReducer} from './cells/cellsPanelReducer';
import {CellsPanelState} from './cells/cellsPanelState';
import {composerPanelReducer} from './composer/composerPanelReducer';
import {ComposerPanelState} from './composer/composerPanelState';
import {contactCardPanelReducer} from './contacts/contactCard/contactCardPanelReducer';
import {ContactCardPanelState} from './contacts/contactCard/contactCardPanelState';
import {contactDetailsPanelReducer} from './contacts/contactDetailsPanelReducer';
import {ContactDetailsPanelState} from './contacts/contactDetailsPanelState';
import {contactsManagerPanelReducer} from './contacts/contactsManager/contactsManagerPanelReducer';
import {ContactsManagerPanelState} from './contacts/contactsManager/contactsManagerPanelState';
import {crmPreviewReducer} from './contacts/contactsManager/crmPreview/crmPreviewReducer';
import {CrmPreviewPanelState} from './contacts/contactsManager/crmPreview/crmPreviewState';
import {emlPanelReducer} from './eml/emlPanelReducer';
import {EmlPanelState} from './eml/emlPanelState';
import {inboxesPanelReducer} from './inboxes/inboxesPanelReducer';
import {InboxesPanelState} from './inboxes/inboxesPanelState';
import {legacyPanelReducer} from './legacy/legacyPanelReducers';
import {LegacyPanelState} from './legacy/legacyPanelState';
import {messageViewerPanelReducer} from './message/messageViewerPanelReducer';
import {MessageViewerPanelState} from './message/messageViewerPanelState';
import {isPanelAction, PanelActionsEnum} from './panelsActions';
import {PanelsState, PanelState} from './panelsState';
import {PanelTypesEnum} from './panelStateBase';
import {settingsPanelReducer} from './settings/settingsPanelReducer';
import {SettingsPanelState} from './settings/settingsPanelState';
import {viewerPanelReducer} from './viewer/viewerPanelReducer';
import {ViewerPanelState} from './viewer/viewerPanelState';

/*
 * Types.
 */

type PanelStateMap = {
  [PanelTypesEnum.APP]: AppPanelState;
  [PanelTypesEnum.INBOXES]: InboxesPanelState;
  [PanelTypesEnum.CELLS]: CellsPanelState;
  [PanelTypesEnum.VIEWER]: ViewerPanelState;
  [PanelTypesEnum.MESSAGE]: MessageViewerPanelState;
  [PanelTypesEnum.COMPOSER]: ComposerPanelState;
  [PanelTypesEnum.CALENDARS]: CalendarsPanelState;
  [PanelTypesEnum.ANALYTICS]: AnalyticsPanelState;
  [PanelTypesEnum.EML]: EmlPanelState;
  [PanelTypesEnum.CONTACT_CARD]: ContactCardPanelState;
  [PanelTypesEnum.CONTACT_DETAILS]: ContactDetailsPanelState;
  [PanelTypesEnum.CONTACTS_MANAGER]: ContactsManagerPanelState;
  [PanelTypesEnum.SETTINGS]: SettingsPanelState;
  [PanelTypesEnum.LEGACY]: LegacyPanelState;
  [PanelTypesEnum.ACCOUNT_DETAILS]: AccountDetailsPanelState;
  [PanelTypesEnum.CRM_PREVIEW]: CrmPreviewPanelState;
  // TODO
  [PanelTypesEnum.SCHEDULING]: PanelState;
};

type PanelReducer<T extends PanelTypesEnum> = (
  state: PanelStateMap[T],
  action: AppAction,
  dataCascade: DataCascade,
) => PanelStateMap[T] | undefined;

type PanelReducerMap = {[T in PanelTypesEnum]: PanelReducer<T>};

/*
 * Panel type matchers.
 */

const stateReducers: PanelReducerMap = {
  [PanelTypesEnum.APP]: appPanelReducer,
  [PanelTypesEnum.INBOXES]: inboxesPanelReducer,
  [PanelTypesEnum.CELLS]: cellsPanelReducer,
  [PanelTypesEnum.VIEWER]: viewerPanelReducer,
  [PanelTypesEnum.MESSAGE]: messageViewerPanelReducer,
  [PanelTypesEnum.COMPOSER]: composerPanelReducer,
  [PanelTypesEnum.CALENDARS]: calendarsPanelReducer,
  [PanelTypesEnum.ANALYTICS]: analyticsPanelReducer,
  [PanelTypesEnum.EML]: emlPanelReducer,
  [PanelTypesEnum.CONTACT_CARD]: contactCardPanelReducer,
  [PanelTypesEnum.CONTACT_DETAILS]: contactDetailsPanelReducer,
  [PanelTypesEnum.CONTACTS_MANAGER]: contactsManagerPanelReducer,
  [PanelTypesEnum.SETTINGS]: settingsPanelReducer,
  [PanelTypesEnum.LEGACY]: legacyPanelReducer,
  [PanelTypesEnum.ACCOUNT_DETAILS]: accountDetailsPanelReducer,
  [PanelTypesEnum.CRM_PREVIEW]: crmPreviewReducer,
  // TODO
  [PanelTypesEnum.SCHEDULING]: identity,
};

/*
 * Main panels reducer.
 */

export function panelsReducer(
  state: PanelsState = {},
  action: AppAction,
  dataCascade: DataCascade,
): PanelsState {
  switch (action.type) {
    // If this is a GC action, update directly.
    case AppActionsEnum.GC:
      return action.payload.panels;

    // Add a new panel with an already existing state.
    case PanelActionsEnum.SET_PANEL:
      return {
        ...state,
        [action.payload.id]: action.payload,
      };

    // If we're removing a panel instead, omit it from the state.
    case PanelActionsEnum.REMOVE_PANEL:
      return omit(state, action.payload);

    default: {
      // If this is a panel action, only target the correct panel.
      if (isPanelAction(action)) {
        const panelState = state[action.payload.panelId];
        if (!panelState) {
          return state;
        }

        const reducer = findReducer(panelState.type);
        const newPanelState = reducer(panelState, action, dataCascade);
        if (newPanelState === panelState) {
          return state;
        }

        // If the reducer returns undefined, remove the panel.
        if (!newPanelState) {
          return omit(state, action.payload.panelId);
        }

        return {
          ...state,
          [action.payload.panelId]: newPanelState,
        };
      }

      // Otherwise, reduce each panel.
      const newState: PanelsState = {};
      forEach(state, (panelState, panelId) => {
        if (!panelState) {
          return;
        }

        const reducer = findReducer(panelState.type);
        const newPanelState = reducer(panelState, action, dataCascade);

        // If the reducer returns undefined, omit the panel.
        if (!newPanelState) {
          return;
        }

        newState[panelId] = newPanelState;
      });

      if (isShallowEqual(newState, state)) {
        return state;
      }

      return newState;
    }
  }
}

function findReducer<T extends PanelTypesEnum>(type: T): PanelReducer<T> {
  return stateReducers[type] as unknown as PanelReducer<T>;
}
